/**
 * The `pipeline-mgmt-api` replay port — re-process transactions through a
 * pipeline (`POST /v0/replays`, drained asynchronously by the replay worker).
 *
 * The dashboard's only caller today is "re-check this group for the missing
 * leg". A group that timed out is a group the engine GUESSED would never
 * complete, and the way to withdraw that guess is to put its legs back through
 * the pipeline with `replay_mode=force` — the only mode whose transitions can
 * move a group off `timed_out`. The API resolves the group to its member legs,
 * so the caller sends the group id and never has to know them.
 *
 * Deliberately thin: the job is queued, not awaited. A reopened group changes
 * state when the worker's child runs land, which is seconds-to-minutes later —
 * so the UI reports "queued", not "reopened".
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { asNullableString, asRecord, asString } from "./wire";

/** Wire values of `commons.interfaces.models.replay_job.ReplayJobStatus`. */
export type ReplayJobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

export interface ReplayJobSummary {
  id: string;
  pipelineId: string;
  status: ReplayJobStatus;
  /** Legs the API resolved for this job — what the worker will re-run. */
  targetTxIds: string[];
  /** The group this job was asked for, when it was a group replay. */
  targetGroupId: string | null;
}

export interface ReplaysSource {
  /**
   * Queue a forced replay of every leg of `groupId`.
   *
   * Rejects server-side for a group whose state cannot reopen (`cancelled` /
   * `reorged` / `completed` / a live one), for a group the caller cannot see,
   * and for one with no member legs — the error message names which.
   */
  reopenGroup(groupId: string, pipelineId: string): Promise<ReplayJobSummary>;
}

function jobFromWire(raw: Record<string, unknown>): ReplayJobSummary {
  return {
    id: asString(raw.id),
    pipelineId: asString(raw.pipeline_id),
    status: asString(raw.status, "queued") as ReplayJobStatus,
    targetTxIds: Array.isArray(raw.target_tx_ids) ? raw.target_tx_ids.map(String) : [],
    targetGroupId: asNullableString(raw.target_group_id),
  };
}

export function createApiReplaysSource(client: ApiClient): ReplaysSource {
  return {
    async reopenGroup(groupId: string, pipelineId: string): Promise<ReplayJobSummary> {
      const res = await client.post<unknown>("/replays", {
        pipeline_id: pipelineId,
        target_kind: "group",
        target_group_id: groupId,
        // Sent for explicitness even though the API forces it for a group
        // target: a reader of this call should not have to know that rule to
        // see that a reopen is a FORCED replay.
        replay_mode: "force",
      });
      return jobFromWire(asRecord(unwrapData(res)));
    },
  };
}
